/**
 * 예매 관련 Read
 *
 * - getTicketReservationsByLiveId() - 공연별 예매 목록 (GET /api/ticket?liveId=)
 * - getTicketReservationById() - 예매 단건 (GET /api/ticket/:id)
 * - getMyTicketReservations() - 내 예매 목록 (GET /api/ticket?regId=)
 * - getTicketReservationsByLiveIdAndRegId() - 공연 + regId(이메일) (GET /api/ticket?liveId=&regId=)
 * - getTotalReservedQuantityForLive() - 공연별 집계 매수 (GET ?liveId=&forStock=1, 서버에서 집계 규칙)
 * - createdAtToSeconds() - createdAt 필드 정렬용 초 단위 변환
 */

import apiClient from "@/utils/api";

/**
 * @param {unknown} body
 * @returns {Array<{ id: string } & Record<string, unknown>>}
 */
function extractTicketListFromResponseBody(body) {
  const raw =
    body?.data !== undefined
      ? body.data
      : Array.isArray(body)
        ? body
        : body?.items ?? body?.reservations;

  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((item) => {
      const id = item?.id ?? item?.documentId;
      return id ? { ...item, id } : null;
    })
    .filter(Boolean);
}

/**
 * 예매 문서의 createdAt을 정렬용 Unix 초로 변환
 * (Firestore Timestamp, { seconds }, Admin JSON {_seconds}, ISO 문자열 등)
 * @param {unknown} createdAt
 * @returns {number}
 */
export function createdAtToSeconds(createdAt) {
  if (!createdAt) return 0;
  if (typeof createdAt === "object") {
    if (typeof createdAt.toDate === "function") {
      const d = createdAt.toDate();
      return Math.floor(d.getTime() / 1000);
    }
    const s = createdAt.seconds ?? createdAt._seconds;
    if (s != null) return Number(s) || 0;
  }
  const t = new Date(createdAt).getTime();
  return Number.isFinite(t) ? Math.floor(t / 1000) : 0;
}

/**
 * 공연(live)별 예매 목록 조회
 * @param {string} liveId
 * @param {{ forStock?: boolean }} [options] `forStock: true` → `forStock=1` (서버가 재고·집계용 목록만 내려줌)
 * @returns {Promise<Array<{ id: string } & Record<string, unknown>>>}
 */
export async function getTicketReservationsByLiveId(liveId, options = {}) {
  if (!liveId) return [];

  const params = { liveId };
  if (options.forStock) {
    params.forStock = 1;
  }

  const response = await apiClient.get("/api/ticket", { params });

  return extractTicketListFromResponseBody(response.data);
}

/**
 * 공연별 예매 매수 합 (한도·매진·submitForm) — `GET /api/ticket?liveId=&forStock=1` 후 quantity 합산
 *
 * @param {string} liveId
 * @returns {Promise<number>}
 */
export async function getTotalReservedQuantityForLive(liveId) {
  if (!liveId) return 0;
  const list = await getTicketReservationsByLiveId(liveId, { forStock: true });
  return list.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
}

/**
 * 공연 + 등록 이메일(regId)로 예매 목록 — 예매 확인 뷰 등
 * @param {string} liveId
 * @param {string} regId
 * @returns {Promise<Array<{ id: string } & Record<string, unknown>>>}
 */
export async function getTicketReservationsByLiveIdAndRegId(liveId, regId) {
  if (!liveId || !regId) return [];

  const response = await apiClient.get("/api/ticket", {
    params: { liveId, regId },
  });

  const list = extractTicketListFromResponseBody(response.data);
  list.sort(
    (a, b) => createdAtToSeconds(b.createdAt) - createdAtToSeconds(a.createdAt)
  );
  return list;
}

/**
 * 예매 단건 조회
 * @param {string} reservationId
 * @returns {Promise<({ id: string } & Record<string, unknown>) | null>}
 */
export async function getTicketReservationById(reservationId) {
  if (!reservationId) return null;

  try {
    const response = await apiClient.get(
      `/api/ticket/${encodeURIComponent(reservationId)}`
    );
    const body = response.data;
    const raw = body?.data !== undefined ? body.data : body;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const id = raw.id ?? raw.documentId ?? reservationId;
    return { ...raw, id };
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * 내 예매 목록 — 등록 이메일(regId) 기준 `GET /api/ticket?regId=`
 *
 * @param {string | null | undefined} regId 예매에 저장된 regId(보통 로그인 이메일)
 * @returns {Promise<Array<{ id: string } & Record<string, unknown>>>}
 */
export async function getMyTicketReservations(regId) {
  if (!regId) return [];

  const response = await apiClient.get("/api/ticket", {
    params: { regId },
  });

  const list = extractTicketListFromResponseBody(response.data);
  list.sort(
    (a, b) => createdAtToSeconds(b.createdAt) - createdAtToSeconds(a.createdAt)
  );
  return list;
}
